import React, { CSSProperties, useState } from 'react';
import ModalSymphony from 'elements/ModalSymphony';
import { TikTokProfileForOnboarding, InstagramProfileForOnboarding, SpotifyArtistSearchResponse } from 'modules/Types';
import Colors from 'modules/Colors';
import { clsx, styx } from 'modules/Utils';
import { toastSuccess } from 'modules/Toast';
import useOrganizationV2 from 'Hooks/useOrganizationV2';
import { SocialConnectType, TEAM_DEFAULT_PRICE_PER_BRAND_IN_DOLLARS, TEAM_MAX_BRANDS_ALLOWED_DEFAULT } from 'modules/Const';
import useScreen from 'Hooks/useScreen';
import { Body2, Subtitle1, Subtitle2 } from 'components/shareable/Typography';
import Icon from 'elements/Icon';
import SocialConnectModal from 'components/shareable/SocialConnectModal';
import { AnySocialConnectProfile } from 'modules/Types';
import TagSymphony from 'elements/TagSymphony';
import SignupWithoutConnections from 'pages/AddArtist/Components/ConnectPlatformModal/SignupWithoutConnections';
import Banner from 'elements/Banner';


type ConnectionStep = 'spotify' | 'tiktok' | 'instagram' | null;

interface ConnectionData {
    spotify?: SpotifyArtistSearchResponse;
    tiktok?: TikTokProfileForOnboarding;
    instagram?: InstagramProfileForOnboarding;
}

interface AddBrandToOrganizationModalV2Props {
    /** Optional class name for styling */
    className?: string;
    /** Controls modal visibility */
    isOpen: boolean;
    /** Handler for closing the modal */
    onClose: () => void;
    /** Callback fired when brands are successfully added to organization */
    onFinishAddingToOrganization?: () => void;
}

const AddBrandToOrganizationModalV2 = ({
    className,
    isOpen,
    onClose,
    onFinishAddingToOrganization = () => { },
}: AddBrandToOrganizationModalV2Props) => {
    const { mobileView } = useScreen();
    const { organization, createOrganizationBrand } = useOrganizationV2();

    // Get organization-specific pricing data, fallback to constants if not available
    const maxBrandsAllowed = organization?.max_brands_allowed || TEAM_MAX_BRANDS_ALLOWED_DEFAULT;
    const pricePerBrand = organization?.price_per_brand || TEAM_DEFAULT_PRICE_PER_BRAND_IN_DOLLARS;

    const [isLoading, setIsLoading] = useState(false);
    
    // Profile name and connections
    const [profileName, setProfileName] = useState('');
    const [connections, setConnections] = useState<ConnectionData>({});
    const [activeConnectionModal, setActiveConnectionModal] = useState<ConnectionStep>(null);
    const [showSignupWithoutConnectionsModal, setShowSignupWithoutConnectionsModal] = useState(false);

    const createBrandWithOrganization = async () => {
        setIsLoading(true);
        try {
            // Determine brand name for toast message (same logic as in useOrganizationV2)
            let brandName = profileName.trim();
            if (!brandName) {
                if (connections.spotify) {
                    brandName = connections.spotify.name;
                } else if (connections.tiktok) {
                    brandName = connections.tiktok.username;
                } else if (connections.instagram) {
                    brandName = connections.instagram.username;
                }
            }

            await createOrganizationBrand({
                profileName,
                connections,
                onFinishAddingToOrganization
            });

            // Reset form state after successful creation
            setProfileName('');
            setConnections({});
            setActiveConnectionModal(null);
            setShowSignupWithoutConnectionsModal(false);

            // Show success toast
            if (brandName) {
                toastSuccess(`${brandName} added successfully!`);
            }

            onClose();
        } catch (error) {
            console.error('Failed to create brand:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleConnectionSelect = (type: ConnectionStep, profile: AnySocialConnectProfile) => {
        if (!type) return;
        
        const newConnections = { ...connections };
        
        switch (type) {
            case 'spotify':
                newConnections.spotify = profile as SpotifyArtistSearchResponse;
                break;
            case 'tiktok':
                newConnections.tiktok = profile as TikTokProfileForOnboarding;
                break;
            case 'instagram':
                newConnections.instagram = profile as InstagramProfileForOnboarding;
                break;
        }
        
        setConnections(newConnections);
        setActiveConnectionModal(null);
    };

    const removeConnection = (type: ConnectionStep) => {
        if (!type) return;
        
        const newConnections = { ...connections };
        delete newConnections[type];
        setConnections(newConnections);
    };



    const hasAnyConnection = Object.keys(connections).length > 0 || profileName.trim().length > 0;
    const canCreateBrand = hasAnyConnection;

    const handleSignupWithoutConnections = (name: string) => {
        setProfileName(name);
        setShowSignupWithoutConnectionsModal(false);
    };

    const renderMainContent = () => (
        <div className="mainContainer" style={sty.stepContainer}>
            {/* Connection Options */}
            <div className="connectionOptionsSection" style={sty.connectionOptionsSection}>
                <div className="connectionTags" style={sty.connectionTags}>
                    <TagSymphony
                        className="connectSpotifyTag"
                        title={connections.spotify ? `${connections.spotify.name}` : 'Connect Spotify'}
                        icon="spotify"
                        iconSize={20}
                        color={connections.spotify ? 'green' : 'purple'}
                        size="medium"
                        onClick={() => setActiveConnectionModal('spotify')}
                        style={sty.largeTag}
                        onRemove={connections.spotify ? () => removeConnection('spotify') : undefined}
                    />
                    <TagSymphony
                        className="connectTikTokTag"
                        title={connections.tiktok ? `@${connections.tiktok.username}` : 'Connect TikTok'}
                        icon="tiktok"
                        iconSize={20}
                        color={connections.tiktok ? 'green' : 'purple'}
                        size="medium"
                        onClick={() => setActiveConnectionModal('tiktok')}
                        style={sty.largeTag}
                        onRemove={connections.tiktok ? () => removeConnection('tiktok') : undefined}
                    />
                    <TagSymphony
                        className="connectInstagramTag"
                        title={connections.instagram ? `@${connections.instagram.username}` : 'Connect Instagram'}
                        icon="instagram"
                        iconSize={20}
                        color={connections.instagram ? 'green' : 'purple'}
                        size="medium"
                        onClick={() => setActiveConnectionModal('instagram')}
                        style={sty.largeTag}
                        onRemove={connections.instagram ? () => removeConnection('instagram') : undefined}
                    />
                    <TagSymphony
                        className="addByNameTag"
                        title={profileName.trim() ? profileName.trim() : 'Add by Name'}
                        icon="edit"
                        iconSize={20}
                        color={profileName.trim() ? 'green' : 'grey'}
                        size="medium"
                        onClick={() => setShowSignupWithoutConnectionsModal(true)}
                        style={sty.largeTag}
                        onRemove={profileName.trim() ? () => setProfileName('') : undefined}
                    />
                </div>
            </div>

            {/* Brand limit cost banner - moved here to be right below the pills */}
            {organization && organization.brands && 
             (organization.brands.length + 1) > maxBrandsAllowed && (
                <Banner 
                    className="addBrandModalBanner" 
                    color={Colors.yellowLight}
                >
                    <div className="flex flex-col gap-1">
                        <Subtitle1>Adding this brand will increase your cost</Subtitle1>
                        <Body2 color={Colors.textColor}>
                            You'll be charged ${pricePerBrand} per month for this additional brand
                        </Body2>
                    </div>
                    <div className="flex gap-2 justify-between">
                        <Body2>Extra brand</Body2>
                        <Subtitle2>${pricePerBrand}/month</Subtitle2>
                    </div>
                </Banner>
            )}
        </div>
    );





    const getModalTitle = () => {
        return 'Add Brand to Team';
    };

    const getModalSubtitle = () => {
        return 'Connect your social platforms or add by name to create your brand profile';
    };

    const renderButtonLeft = () => {
        return undefined;
    };

    const renderButtonRight = () => {
        return hasAnyConnection ? 'Create Brand' : 'Connect a Platform';
    };

    const handleButtonLeftClick = () => {
        // No left button in single step flow
    };

    const handleButtonRightClick = () => {
        if (canCreateBrand && !isLoading) {
            createBrandWithOrganization();
        }
    };

    const isButtonRightDisabled = () => {
        return !canCreateBrand || isLoading;
    };

    return (
        <>
       
            <ModalSymphony
                className={clsx('addBrandToOrganizationModalV2', className)}
                isOpen={isOpen}
                onClose={onClose}
                title={getModalTitle()}
                subtitle={getModalSubtitle()}
                headerIcon="tada"
                onClickButtonLeft={undefined}
                buttonLeftText={renderButtonLeft()}
                buttonLeftVariant="outlined"
                onClickButtonRight={handleButtonRightClick}
                buttonRightText={renderButtonRight()}
                buttonRightVariant="primary"
                buttonRightDisabled={isButtonRightDisabled()}
                buttonRightIsLoading={isLoading}
            >
                {renderMainContent()}
            </ModalSymphony>

            {/* Connection Modals */}
            {activeConnectionModal && (
                <SocialConnectModal
                    className="connectionModal"
                    isOpen={!!activeConnectionModal}
                    onClose={() => setActiveConnectionModal(null)}
                    socialConnectType={
                        activeConnectionModal === 'spotify' ? SocialConnectType.SPOTIFY :
                        activeConnectionModal === 'tiktok' ? SocialConnectType.TIKTOK :
                        SocialConnectType.INSTAGRAM
                    }
                    isDirectConnect
                    isConnectToBrand={false}
                    onConnect={(profile) => handleConnectionSelect(activeConnectionModal, profile)}
                />
            )}
            
            {/* Signup Without Connections Modal */}
            <SignupWithoutConnections
                className="signupWithoutConnectionsModal"
                open={showSignupWithoutConnectionsModal}
                closeModal={() => setShowSignupWithoutConnectionsModal(false)}
                onSignupWithoutConnections={handleSignupWithoutConnections}
            />
        </>
    );
};

const sty: Record<string, CSSProperties> = {
    stepContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
        padding: 16,
    },
    connectionOptionsSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },
    connectionTags: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 12,
    },
    largeTag: {
        height: 48,
        fontSize: 16,
        paddingLeft: 20,
        paddingRight: 20,
        minWidth: 160,
    },

};

export default AddBrandToOrganizationModalV2;
